<template>
  <section class="ai-visibility-question-mentioned-brands">
    <div class="ai-visibility-question-mentioned-brands__heading">
      <h3>Who AI mentioned</h3>
      <p>Brands ranked by how often they appear in answers</p>
    </div>
    <div v-if="brands.length" class="ai-visibility-question-mentioned-brands__list">
      <div
          v-for="(brand, index) in brands"
          :key="`${brand.domain}-${index}`"
          class="ai-visibility-question-mentioned-brands__row"
          :class="{ 'ai-visibility-question-mentioned-brands__row--tracked': isTrackedDomain(brand.domain) }"
      >
        <div class="ai-visibility-question-mentioned-brands__brand">
          <span class="ai-visibility-question-mentioned-brands__rank">{{ index + 1 }}.</span>
          <source-avatar :domain="brand.domain"/>
          <strong>{{ brand.brandName || brand.domain }}</strong>
          <panel-badge v-if="isTrackedDomain(brand.domain)">YOU</panel-badge>
        </div>
        <div class="ai-visibility-question-mentioned-brands__score">
          <div class="progress-bar">
            <progress-scale
                :value="brand.visibilityScore"
                :tone="getVisibilityScoreTone(brand.visibilityScore)"
            />
          </div>
          <span class="score-value" :class="`score-value-${getVisibilityScoreTone(brand.visibilityScore)}`">{{ brand.visibilityScore }}</span>
        </div>
      </div>
    </div>
    <p v-else class="ai-visibility-question-mentioned-brands__empty">
      No mentioned brands for this question yet.
    </p>
  </section>
</template>

<script>
import SourceAvatar from '../../../ui/display/SourceAvatar.vue'
import PanelBadge from '../../../ui/display/PanelBadge.vue'
import ProgressScale from "../../../ui/display/ProgressScale.vue";

export default {
  name: 'QuestionMentionedBrandsList',
  components: {
    ProgressScale,
    SourceAvatar,
    PanelBadge,
  },
  props: {
    brands: {
      type: Array,
      default() {
        return []
      },
    },
    trackedDomain: {
      type: String,
      default: '',
    },
    askedCount: {
      type: Number,
      default: 0
    }
  },
  methods: {
    isTrackedDomain(domain) {
      return Boolean(domain && this.trackedDomain && domain === this.trackedDomain)
    },
    getVisibilityScoreTone(score) {
      if (score > 60) {
        return 'success'
      } else if (score > 30) {
        return 'warning'
      } else {
        return 'danger'
      }
    }
  },
}
</script>

<style lang="scss" scoped>

.ai-visibility-question-mentioned-brands__score {
  display: flex;
  align-items: center;
  gap: 8px;

  .progress-bar {
    width: 100px;
  }

  .score-value {
    font-size: 16px;
    font-weight: 700;
  }

  .score-value-success {
    color: var(--v-green7Color-base);
  }

  .score-value-danger {
    color: #FF4040;
  }

  .score-value-warning {
    color: var(--v-profileAnchorColor-base);
  }
}

.ai-visibility-question-mentioned-brands {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ai-visibility-question-mentioned-brands__heading {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ai-visibility-question-mentioned-brands__heading h3,
.ai-visibility-question-mentioned-brands__heading p {
  margin: 0;
}

.ai-visibility-question-mentioned-brands__heading h3 {
  color: var(--v-mainColor-base);
  font-size: 16px;
  font-weight: 700;
}

.ai-visibility-question-mentioned-brands__heading p,
.ai-visibility-question-mentioned-brands__empty {
  color: var(--v-mainColor3-base);
  font-size: 14px;
  line-height: 1.45;
}

.ai-visibility-question-mentioned-brands__list {
  overflow: hidden;
  border: 1px solid var(--v-gray13Color-base);
  border-radius: 8px;
  background: var(--v-cardBg-base);
}

.ai-visibility-question-mentioned-brands__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
  border-bottom: 1px dashed var(--v-gray12Color-base);
  padding: 12px;
}

.ai-visibility-question-mentioned-brands__row:last-child {
  border-bottom: 0;
}

.ai-visibility-question-mentioned-brands__row--tracked {
  background: var(--v-pink1Color-base);
}

.ai-visibility-question-mentioned-brands__brand {
  flex: 1 1 auto;
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
}

.ai-visibility-question-mentioned-brands__rank {
  width: 22px;
  flex: 0 0 22px;
  color: var(--v-mainColor-base);
  font-size: 14px;
}

.ai-visibility-question-mentioned-brands__brand ::v-deep .ai-visibility-source-avatar {
  width: 22px;
  height: 22px;
  flex-basis: 22px;
  font-size: 10px;
}

.ai-visibility-question-mentioned-brands__brand strong {
  overflow: hidden;
  color: var(--v-mainColor-base);
  font-size: 14px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 460px) {
  .ai-visibility-question-mentioned-brands__row {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
